import { useRef } from 'react';

import {
  COGNITIVE_BEHAVIORAL_VERBS,
  AFFECTIVE_BEHAVIORAL_VERBS,
  PSYCHOMOTOR_BEHAVIORAL_VERBS,
} from 'apps/web/constants/forms/objective/behavioralVerbs';
import OBJECTIVE_FORM_KEYS from 'apps/web/constants/forms/objective/formKeys';
import useAutosizeTextArea from 'apps/web/hooks/useAutoSizeTextArea';
import processInput from 'apps/web/utils/formInput';

import {
  LC_PAGE_COMPETENCY_CHAR_LIMIT,
  LC_PAGE_SUBJECT_CHAR_LIMIT,
} from '@lessonPlanner/constants';

import {
  CategorizedComboboxField,
  TextAreaField,
  TextField,
} from '../form-fields';
import TranslationDropdown from '../molecules/TranslationDropdown';
import { GradeLevelDropdown } from '../Select';
export default function LocalObjective({
  errors,
  watchedValues,
  triggerValidation,
  setValue,
}: {
  errors;
  watchedValues;
  triggerValidation;
  setValue;
}) {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useAutosizeTextArea(textAreaRef.current, watchedValues.competency);

  return (
    <>
      <TextField
        className="col-span-full"
        errorMessage={!!errors.subject && 'Please enter the Subject'}
        id={OBJECTIVE_FORM_KEYS.subject}
        label="Subject"
        maxLength={LC_PAGE_SUBJECT_CHAR_LIMIT}
        name={OBJECTIVE_FORM_KEYS.subject}
        placeholder={'Input the subject for this lesson plan'}
        type="subject"
        value={watchedValues.subject}
        isMaxLengthReached={
          watchedValues.subject?.length >= LC_PAGE_SUBJECT_CHAR_LIMIT
        }
        onBlur={() =>
          processInput(
            setValue,
            triggerValidation,
            OBJECTIVE_FORM_KEYS.subject,
            watchedValues.subject?.trim() || '',
          )
        }
        onChange={(e) =>
          processInput(
            setValue,
            triggerValidation,
            OBJECTIVE_FORM_KEYS.subject,
            e.target.value,
          )
        }
      />

      <GradeLevelDropdown
        error={!!errors.gradeLevel}
        errorMessage={!!errors.gradeLevel && 'Please select the Grade Level'}
        value={watchedValues.gradeLevel}
        onChange={(value) => setValue(OBJECTIVE_FORM_KEYS.gradeLevel, value)}
        onBlur={() => {
          triggerValidation(OBJECTIVE_FORM_KEYS.gradeLevel);
        }}
      />

      <TextAreaField
        className="col-span-full"
        error={!!errors.competency}
        id={OBJECTIVE_FORM_KEYS.competency}
        label="Learning Competency"
        maxLength={LC_PAGE_COMPETENCY_CHAR_LIMIT}
        name={OBJECTIVE_FORM_KEYS.competency}
        placeholder="Input the learning competency"
        rows={2}
        textAreaRef={textAreaRef}
        type="competency"
        value={watchedValues.competency}
        errorMessage={
          !!errors.competency && 'Please enter the Learning Competency'
        }
        isMaxLengthReached={
          watchedValues.competency?.length >= LC_PAGE_COMPETENCY_CHAR_LIMIT
        }
        onBlur={() =>
          processInput(
            setValue,
            triggerValidation,
            OBJECTIVE_FORM_KEYS.competency,
            watchedValues.competency?.trim() || '',
          )
        }
        onChange={(e) =>
          processInput(
            setValue,
            triggerValidation,
            OBJECTIVE_FORM_KEYS.competency,
            e.target.value,
          )
        }
      />

      <CategorizedComboboxField
        multiSelect
        twoColumnLayout
        className="col-span-full"
        id={OBJECTIVE_FORM_KEYS.cognitiveVerbs}
        label="Cognitive "
        maxSelections={3}
        options={COGNITIVE_BEHAVIORAL_VERBS}
        placeholder="Select or search for Cognitive Behavioral Verbs"
        subLabel="Select a maximum of 3 verbs"
        value={watchedValues.cognitiveVerbs}
        onChange={(value) =>
          setValue(OBJECTIVE_FORM_KEYS.cognitiveVerbs, value)
        }
      />

      <CategorizedComboboxField
        multiSelect
        twoColumnLayout
        className="col-span-full"
        id={OBJECTIVE_FORM_KEYS.affectiveVerbs}
        label="Affective"
        maxSelections={3}
        options={AFFECTIVE_BEHAVIORAL_VERBS}
        placeholder="Select or search for Affective Behavioral Verbs"
        subLabel="Select a maximum of 3 verbs"
        value={watchedValues.affectiveVerbs}
        onChange={(value) =>
          setValue(OBJECTIVE_FORM_KEYS.affectiveVerbs, value)
        }
      />

      <CategorizedComboboxField
        multiSelect
        twoColumnLayout
        className="col-span-full"
        id={OBJECTIVE_FORM_KEYS.psychomotorVerbs}
        label="Psychomotor"
        maxSelections={3}
        options={PSYCHOMOTOR_BEHAVIORAL_VERBS}
        placeholder="Select or search for Psychomotor Behavioral Verbs"
        subLabel="Select a maximum of 3 verbs"
        value={watchedValues.psychomotorVerbs}
        onChange={(value) =>
          setValue(OBJECTIVE_FORM_KEYS.psychomotorVerbs, value)
        }
      />

      <TranslationDropdown
        errors={errors}
        handleTriggerValidation={(fieldName) => triggerValidation(fieldName)}
        language={watchedValues.language}
        handleUpdateFieldValue={(fieldName, value) =>
          setValue(fieldName, value)
        }
      />
    </>
  );
}
